import React, { useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { t } from '../lib/i18n';
import LoginScreen from './LoginScreen';
import ChangePasswordModal from './ChangePasswordModal';

/**
 * AuthGate — wraps the app and gates access behind authentication.
 *
 * States: 'loading' | 'login' | 'ok' | 'must-change'
 *
 * Props:
 *   children  — rendered when authenticated (state='ok')
 *   onLogin   — callback(user) when user info becomes available
 */
export default function AuthGate({ children, onLogin }) {
    const [state, setState] = React.useState('loading'); // loading | login | ok | must-change
    const [sessionExpiredMsg, setSessionExpiredMsg] = React.useState(false);

    // Auto-refresh cookie every 12 hours (if active)
    useEffect(() => {
        if (state !== 'ok') return;
        const interval = setInterval(async () => {
            try {
                const r = await authFetch('/api/auth/refresh', { method: 'POST', body: '{}' });
                if (!r.ok) {
                    setSessionExpiredMsg(true);
                    setState('login');
                }
            } catch(e) { /* ignore network errors during refresh */ }
        }, 12 * 60 * 60 * 1000); // 12 hours
        return () => clearInterval(interval);
    }, [state]);

    // Check auth on load — cookie sent automatically
    useEffect(() => {
        authFetch('/api/auth/me')
            .then(r => {
                if (r.status === 401) {
                    return r.json().then(data => {
                        if (data.code === 'TOKEN_EXPIRED') setSessionExpiredMsg(true);
                        setState('login');
                    });
                }
                return r.json().then(data => {
                    if (data.user) {
                        if (onLogin) onLogin(data.user);
                        if (data.user.mustChangePassword) setState('must-change');
                        else setState('ok');
                    } else {
                        setState('login');
                    }
                });
            })
            .catch(() => setState('login'));
    }, []);

    if (state === 'loading') return (
        <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]">
            <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-[#AA5D26] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-[#9CA3AF]">{t('auth_loading')}</p>
            </div>
        </div>
    );

    if (state === 'must-change') return (
        <ChangePasswordModal forceChange={true} onSuccess={() => setState('ok')} />
    );

    if (state === 'login') return (
        <React.Fragment>
            {sessionExpiredMsg && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 shadow-lg animate-fade-in">
                    {t('auth_session_expired')}
                </div>
            )}
            <LoginScreen
                sessionExpiredMsg={sessionExpiredMsg}
                onLogin={(user, mustChange) => {
                    setSessionExpiredMsg(false);
                    if (onLogin) onLogin(user);
                    if (mustChange) setState('must-change');
                    else setState('ok');
                }}
            />
        </React.Fragment>
    );

    return children;
}
